import { useState } from 'react';
import { useNotifications } from '@/hooks/useNotifications';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Bell, Loader2, HelpCircle, PlayCircle } from 'lucide-react';
import { TRIGGER_ONBOARDING_EVENT } from './NotificationPermissionModal';

// Detect Mac/Safari
function isMac(): boolean {
  return /Mac|Macintosh/.test(navigator.userAgent) && !('ontouchend' in document);
}

function isSafari(): boolean {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

export function NotificationDiagnostics() {
  const {
    isSupported,
    isEnabled,
    needsIOSInstall,
    isDenied,
    isLoading,
    toggle,
    enable,
  } = useNotifications();
  const [showRetryHint, setShowRetryHint] = useState(false);

  const handleToggle = async (enabled: boolean) => {
    const success = await toggle(enabled);
    if (success) {
      toast.success(enabled ? 'Notifications activées' : 'Notifications désactivées');
      setShowRetryHint(false);
    } else if (enabled) {
      if (isDenied) {
        toast.error('Notifications bloquées par le navigateur');
        setShowRetryHint(true);
      } else if (needsIOSInstall) {
        toast.error('Installez l\'app depuis Safari pour activer les notifications');
      } else {
        toast.error('Impossible d\'activer les notifications');
        setShowRetryHint(true);
      }
    }
  };

  const handleRetryActivation = async () => {
    const success = await enable();
    if (success) {
      toast.success('Notifications activées !');
      setShowRetryHint(false);
    }
  };

  const handleTestOnboarding = () => {
    window.dispatchEvent(new CustomEvent(TRIGGER_ONBOARDING_EVENT));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-between p-4 bg-card rounded-xl border border-border">
        <div className="flex items-center gap-3">
          <Bell className="w-5 h-5 text-muted-foreground" />
          <span className="text-sm">Notifications</span>
        </div>
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isSupported) {
    return (
      <div className="p-4 bg-card rounded-xl border border-border">
        <div className="flex items-center gap-3 text-muted-foreground">
          <Bell className="w-5 h-5" />
          <div>
            <p className="text-sm font-medium">Notifications non disponibles</p>
            <p className="text-xs">Votre navigateur ne supporte pas les notifications push.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Main toggle */}
      <div className="flex items-center justify-between p-4 bg-card rounded-xl border border-border">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isEnabled ? 'bg-primary/10' : 'bg-muted'}`}>
            <Bell className={`w-5 h-5 ${isEnabled ? 'text-primary' : 'text-muted-foreground'}`} />
          </div>
          <div>
            <p className="text-sm font-medium">Notifications push</p>
            <p className="text-xs text-muted-foreground">
              {isEnabled ? 'Activées - vous recevez les alertes' : 'Désactivées'}
            </p>
          </div>
        </div>
        <Switch
          checked={isEnabled}
          onCheckedChange={handleToggle}
          disabled={isLoading || isDenied || needsIOSInstall}
        />
      </div>

      {/* iOS install hint */}
      {needsIOSInstall && (
        <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <p className="text-sm font-medium text-blue-600 mb-1">📱 Installation requise</p>
          <p className="text-xs text-blue-600/80">
            Pour activer les notifications sur iPhone/iPad, installez d'abord l'application :
          </p>
          <ol className="text-xs text-blue-600/80 mt-2 space-y-1 list-decimal list-inside">
            <li>Appuyez sur <strong>Partager</strong> (📤)</li>
            <li>Sélectionnez <strong>"Sur l'écran d'accueil"</strong></li>
            <li>Ouvrez l'app depuis l'écran d'accueil</li>
          </ol>
        </div>
      )}

      {/* Permission denied hint */}
      {isDenied && !needsIOSInstall && (
        <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <p className="text-sm font-medium text-amber-600 mb-1">🔒 Notifications bloquées</p>
          <p className="text-xs text-amber-600/80">
            Les notifications sont bloquées par votre navigateur. Pour les réactiver :
          </p>
          <ol className="text-xs text-amber-600/80 mt-2 space-y-1 list-decimal list-inside">
            <li>Cliquez sur l'icône 🔒 dans la barre d'adresse</li>
            <li>Trouvez "Notifications" et changez en "Autoriser"</li>
            <li>Rechargez la page</li>
          </ol>
        </div>
      )}

      {/* Mac/Safari specific hint */}
      {isMac() && isSafari() && !isEnabled && !isDenied && (
        <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
          <p className="text-sm font-medium text-purple-600 mb-1">🍎 Safari sur Mac</p>
          <p className="text-xs text-purple-600/80">
            Safari sur Mac supporte les notifications web. Assurez-vous que :
          </p>
          <ol className="text-xs text-purple-600/80 mt-2 space-y-1 list-decimal list-inside">
            <li>macOS 13+ (Ventura) et Safari 16+</li>
            <li>Notifications autorisées dans Préférences Système → Notifications → Safari</li>
            <li>Le site a la permission d'envoyer des notifications</li>
          </ol>
        </div>
      )}

      {/* Retry activation hint - shown when activation failed but not denied */}
      {showRetryHint && !isDenied && !needsIOSInstall && !isEnabled && (
        <div className="p-3 bg-muted/50 border border-border rounded-lg">
          <div className="flex items-start gap-3">
            <HelpCircle className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground mb-1">L'activation n'a pas fonctionné ?</p>
              <p className="text-xs text-muted-foreground mb-3">
                La popup du navigateur a peut-être été fermée trop vite.
              </p>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={handleRetryActivation}
                className="w-full"
              >
                Réessayer l'activation
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Test onboarding button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleTestOnboarding}
        className="w-full text-muted-foreground hover:text-foreground"
      >
        <PlayCircle className="w-4 h-4 mr-2" />
        Tester l'onboarding
      </Button>
    </div>
  );
}
